'use client';

import { useEffect, useRef } from "react";
import Image from "next/image";
import styles from "./DraggableWindow.module.scss";
import { useDraggable } from "@/lib/hooks/useDraggable";
import { useVideoArming } from "@/lib/hooks/useVideoArming";

interface Props {
  title: string;
  src: string;
  initialPosition: { x: number; y: number };
  size: { width: number; height: number };
  allowDragging: boolean;
  constrainingFunction?: (pos: { x: number; y: number }, size: { width: number; height: number }) => { x: number; y: number };
  href?: string;
  autoplay?: boolean;
  animationDelay?: number;
  zIndex?: number;
  onPointerDownCapture?: () => void;
  isOnLandingPage: boolean;
  prevProjectHref?: string;
  nextProjectHref?: string;
  widthContent: number;
  isCollapsing?: boolean;
  onNavigate?: (href: string) => void;
  eager?: boolean;
  mediaAspectRatio?: number;
}

export default function DraggableWindow({ title, src, initialPosition, size, allowDragging, constrainingFunction, href, autoplay = false, animationDelay = 0, zIndex, onPointerDownCapture, isOnLandingPage, prevProjectHref, nextProjectHref, widthContent, isCollapsing = false, onNavigate, eager = false, mediaAspectRatio }: Props) {
  const { position, isPointerDown, isDraggingRef, onPointerDown } = useDraggable({
    initialPosition,
    allowDragging,
    constrainingFunction,
    size,
    isOnLandingPage,
  });

  const elementRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pointerDownOnThisElementRef = useRef(false);
  const isVideo = src.endsWith('.mp4');

  const { shouldLoadVideo } = useVideoArming({
    videoRef,
    initialLoad: autoplay || eager,
    initialPlay: autoplay,
  });

  // Handle click navigation (only if not dragging)
  useEffect(() => {
    const handleWindowPointerUp = (event: PointerEvent) => {
      const wasPointerDownOnThisElement = pointerDownOnThisElementRef.current;
      const wasDragging = isDraggingRef.current;

      // Only navigate if pointer down was on this element AND pointer up is also on this element
      const pointerUpOnElement = elementRef.current?.contains(event.target as Node);
      if (wasPointerDownOnThisElement && !wasDragging && pointerUpOnElement) {
        event.stopPropagation();
        tryNavigatingToProject();
      }

      pointerDownOnThisElementRef.current = false;
    };

    window.addEventListener('pointerup', handleWindowPointerUp);
    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
    };
  }, []);

  const handlePointerDownOnElement = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerDownOnThisElementRef.current = true;
    onPointerDownCapture?.();
    onPointerDown(event);
  };

  const tryNavigatingToProject = () => {
    if (isDraggingRef.current) {
      return;
    }
    if (!href) {
      return;
    }

    onNavigate?.(href);
  };

  const handleClick = (event: React.MouseEvent) => {
    // Stop click events from propagating to parent elements (e.g., ProjectDetail's returnToLandingPage)
    event.stopPropagation();
  };

  const fontSize = Math.ceil(widthContent / 160);

  return (
    <div
      className={`${styles.draggableWindowWrapper} ${isCollapsing ? styles['collapseWindow'] : styles['expandWindow']}`}
      style={{ 
        left: position.x, 
        top: position.y,
        width: size.width,
        animationDelay: `${animationDelay}s`,
        zIndex: zIndex
      }}
      onClick={handleClick}
    >
      <div
        ref={elementRef}
        className={styles.draggableWindow}
        data-grabbable={allowDragging ? '' : undefined}
        style={{
          cursor: allowDragging ? (isPointerDown ? "grabbing" : "grab") : "pointer", 
          minHeight: size.height,
        }}
        onPointerDown={handlePointerDownOnElement}
      >
        <p className={styles.title} style={{fontSize: fontSize}}>{title}</p>
        <div
          className={styles.mediaContainer}
          style={mediaAspectRatio ? { height: Math.ceil(size.width * mediaAspectRatio) } : undefined}
        >
          {isVideo ? (
            <video 
              ref={videoRef}
              className={styles.media} 
              src={shouldLoadVideo ? src : undefined}
              preload={shouldLoadVideo ? 'metadata' : 'none'}
              muted 
              playsInline 
              loop
              autoPlay={autoplay}
              aria-label={`${title} video`}
            >
              <track
                kind="captions"
                src="/captions/no-audio.vtt"
                srcLang="en"
                label="No spoken audio"
              />
            </video>
          ) : (
            <Image
              className={styles.media}
              src={src}
              alt={title}
              width={2400}
              height={1404}
              sizes="50vw"
              unoptimized={src.endsWith('.gif')}
              loading={eager ? 'eager' : undefined}
            />
          )}
        </div>
      </div>
      {prevProjectHref && onNavigate && (
        <button 
          className={styles['navigation-back']} 
          style={{fontSize: fontSize}}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onNavigate(prevProjectHref)}
        >
          BACK
        </button>
      )}
      {nextProjectHref && onNavigate && (
        <button 
          className={styles['navigation-next']} 
          style={{fontSize: fontSize}}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onNavigate(nextProjectHref)}
        >
          NEXT
        </button>
      )}
    </div>
  );
}
